import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { cloudinaryImg } from '@/utils/cloudinary';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Search, Users, Zap, Building2, CheckCircle2, ArrowRight, Shield, Target, Eye, TrendingUp, Lock, ChevronLeft, ChevronRight, Star, Menu, X, Calendar, Clock } from 'lucide-react';
import AppLogo from '@/components/AppLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentBlogPost, setCurrentBlogPost] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/blog/posts?limit=4`)
      .then(({ data }) => { if (Array.isArray(data.posts)) setBlogPosts(data.posts); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (blogPosts.length < 2) return;
    const t = setInterval(() => setCurrentBlogPost(i => (i + 1) % blogPosts.length), 5000);
    return () => clearInterval(t);
  }, [blogPosts.length]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const testimonials = [
    {
      name: 'Ricardo Mendes',
      role: 'Comprador',
      location: 'Pinheiros, São Paulo',
      text: 'Depois de 6 meses procurando, encontrei meu apartamento em 2 semanas com o MatchImovel. A curadoria realmente funciona, só recebi imóveis que faziam sentido para mim.',
      rating: 5
    },
    {
      name: 'Ana Paula Costa',
      role: 'Corretora',
      location: 'Rio de Janeiro',
      text: 'Consegui vender um apartamento que estava parado há 8 meses. A comissão de 60% e a base de compradores qualificados fizeram toda a diferença no meu faturamento.',
      rating: 5
    },
    {
      name: 'Carlos Eduardo Silva',
      role: 'Comprador',
      location: 'Moema, São Paulo',
      text: 'Impressionante como a equipe entendeu exatamente o que eu precisava. Visitei apenas 3 imóveis e fechei negócio no segundo. Processo transparente e rápido.',
      rating: 5
    },
    {
      name: 'Juliana Rodrigues',
      role: 'Corretora',
      location: 'Belo Horizonte',
      text: 'Melhor plataforma para corretores que trabalham sério. Sem perda de tempo com curiosos, todos os compradores são pré-qualificados e realmente interessados.',
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-sm' 
            : 'bg-transparent'
        }`}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div 
            className="text-2xl font-bold flex items-center gap-1 cursor-pointer"
            onClick={() => scrollToSection('hero')}
          >
            <AppLogo />
            <span><span className="text-slate-900">Match</span><span className="text-indigo-600">Imovel</span></span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('processo')}
              className="text-slate-600 hover:text-indigo-600 font-medium transition-colors"
            >
              Como funciona
            </button>
            <button 
              onClick={() => scrollToSection('compradores')}
              className="text-slate-600 hover:text-indigo-600 font-medium transition-colors"
            >
              Para Compradores
            </button>
            <button 
              onClick={() => scrollToSection('corretores')}
              className="text-slate-600 hover:text-indigo-600 font-medium transition-colors"
            >
              Para Corretores
            </button>
            <button
              onClick={() => scrollToSection('depoimentos')}
              className="text-slate-600 hover:text-indigo-600 font-medium transition-colors"
            >
              Depoimentos
            </button>
            <Link
              to="/blog"
              className="text-slate-600 hover:text-indigo-600 font-medium transition-colors"
            >
              Blog
            </Link>
            <Button
              data-testid="nav-login-button"
              onClick={() => navigate('/login')} 
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6"
            >
              Entrar
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <Button 
              onClick={() => navigate('/login')} 
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm"
            >
              Entrar
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-600"
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t shadow-lg"
          >
            <div className="px-6 py-4 space-y-4">
              <button 
                onClick={() => scrollToSection('processo')}
                className="block w-full text-left text-slate-600 hover:text-indigo-600 font-medium py-2"
              >
                Como funciona
              </button>
              <button 
                onClick={() => scrollToSection('compradores')}
                className="block w-full text-left text-slate-600 hover:text-indigo-600 font-medium py-2"
              >
                Para Compradores
              </button>
              <button 
                onClick={() => scrollToSection('corretores')}
                className="block w-full text-left text-slate-600 hover:text-indigo-600 font-medium py-2"
              >
                Para Corretores
              </button>
              <button
                onClick={() => scrollToSection('depoimentos')}
                className="block w-full text-left text-slate-600 hover:text-indigo-600 font-medium py-2"
              >
                Depoimentos
              </button>
              <Link
                to="/blog"
                className="block w-full text-left text-slate-600 hover:text-indigo-600 font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      <main>
      {/* Hero Section */}
      <div id="hero" className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50 to-white pt-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-200 via-purple-100 to-transparent opacity-60"></div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block px-4 py-2 bg-indigo-100 rounded-full mb-6">
              <p className="text-sm font-semibold text-indigo-600">Nova forma de comprar imóvel</p>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
              O imóvel ideal
              <span className="block text-indigo-600 italic">te encontra.</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-12 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Cansado de enviar mensagens para dezenas de corretores e descobrir que o imóvel já foi vendido?
              Cadastre o que você procura — nossa plataforma e equipe de curadoria trabalham para você.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                data-testid="hero-buyer-cta"
                onClick={() => { sessionStorage.setItem('utm_content', 'comprador'); navigate('/register?role=buyer'); }}
                size="lg"
                className="rounded-full h-14 px-10 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/25 text-lg font-medium"
              >
                Cadastrar meu interesse <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-agent-cta"
                onClick={() => { sessionStorage.setItem('utm_content', 'corretor'); navigate('/register?role=agent'); }}
                size="lg"
                variant="outline"
                className="rounded-full h-14 px-10 text-lg font-medium border-2 border-slate-300 hover:border-indigo-600"
              >
                Sou corretor
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Como Funciona */}
      <div id="processo" className="max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm font-semibold text-indigo-600 mb-4 uppercase tracking-wider">O processo</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Da busca ao
            <span className="text-indigo-600 italic"> fechamento</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            {
              number: '1',
              icon: Search,
              title: 'Comprador cadastra',
              desc: 'Formulário completo que vai além do básico: bairros, metragem e valor. Capturamos seus gostos, necessidades e estilo de vida para encontrar o imóvel que realmente combina com você.',
              color: 'bg-indigo-100 text-indigo-600'
            },
            {
              number: '2',
              icon: Building2,
              title: 'Corretor cadastra',
              desc: 'Corretores cadastram imóveis exclusivos na plataforma — muitas vezes não encontrados em portais ou ainda nem anunciados ao público.',
              color: 'bg-purple-100 text-purple-600'
            },
            {
              number: '3',
              icon: Zap,
              title: 'Match acontece!',
              desc: 'Nosso sistema cruza todas as informações e encontra matches incríveis entre compradores e imóveis. Inteligência que trabalha por você.',
              color: 'bg-green-100 text-green-600'
            },
            {
              number: '4',
              icon: Shield,
              title: 'Curadoria valida',
              desc: 'Nenhum imóvel fora do seu perfil chega até você. Nossa curadoria experiente filtra tudo — e nenhum corretor tem acesso aos seus dados. Você fala apenas com seu curador exclusivo MatchImovel.',
              color: 'bg-blue-100 text-blue-600'
            }
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="p-6 rounded-3xl border-2 hover:border-indigo-200 transition-all h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full ${step.color} flex items-center justify-center flex-shrink-0`}>
                    <step.icon className="w-5 h-5" />
                  </div>
                  <span className="text-2xl font-bold text-indigo-600">{step.number}</span>
                  <h3 className="text-lg font-bold text-slate-900">{step.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed text-sm">{step.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* O Problema */}
      <div className="bg-slate-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-sm font-semibold text-indigo-400 mb-4 uppercase tracking-wider">O problema que resolvemos</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              O mercado é feito de imóveis.
              <span className="block text-indigo-400 italic mt-2">Nós somos feitos de compradores.</span>
            </h2>
            <p className="text-lg text-white leading-relaxed">
              Em vez de mais um portal cheio de anúncios desatualizados, criamos uma vitrine de pessoas reais com intenção real de compra. Corretores encontram compradores. Compradores encontram o imóvel certo. Sem desperdício de tempo.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Para Compradores */}
      <div id="compradores" className="bg-gradient-to-br from-indigo-50 to-purple-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-indigo-600 mb-4 uppercase tracking-wider">Para compradores</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Cadastre o que você quer.
                <span className="block text-indigo-600 italic mt-2">Nós buscamos pra você.</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Sem precisar ficar monitorando portais ou esperando retorno de corretores. Você define o perfil, nós trabalhamos.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: CheckCircle2, text: 'Cadastro gratuito e sem compromisso' },
                  { icon: Shield, text: 'Sugestões curadas por especialistas' },
                  { icon: Eye, text: 'Sem exposição do seu contato' },
                  { icon: Target, text: 'Visitas apenas de imóveis validados' },
                  { icon: Lock, text: 'Proteção contratual da negociação' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 font-medium">{item.text}</p>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => { sessionStorage.setItem('utm_content', 'comprador'); navigate('/register?role=buyer'); }}
                size="lg"
                className="rounded-full h-12 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/25"
              >
                Cadastrar meu interesse <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1758523419991-c6d6dae06626?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBoYXBweSUyMGNvdXBsZSUyMGhvbWUlMjBrZXlzfGVufDB8fHx8MTc3Mjc3NjA1OHww&ixlib=rb-4.1.0&q=75&w=900&h=660&fit=crop&auto=format"
                alt="Casal feliz com chaves"
                width={900}
                height={660}
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-2xl w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Para Corretores */}
      <div id="corretores" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <img
                src="https://images.unsplash.com/photo-1758611972971-1c8b9c6d7822?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwcmVhbCUyMGVzdGF0ZSUyMGFnZW50JTIwc21pbGluZyUyMGxvb2tpbmclMjBhdCUyMHBob25lJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3MzA5Mzk5Mnww&ixlib=rb-4.1.0&q=75&w=900&h=660&fit=crop&auto=format"
                alt="Corretora profissional"
                width={900}
                height={660}
                loading="lazy"
                decoding="async"
                className="rounded-3xl shadow-2xl w-full h-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <p className="text-sm font-semibold text-purple-600 mb-4 uppercase tracking-wider">Para corretores</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Encontre compradores para
                <span className="block text-purple-600 italic mt-2">aquele imóvel difícil.</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Acesse uma base real de compradores qualificados. Faça buscas, identifique matches e feche mais negócios.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Users, text: 'Acesso à base de compradores ativos' },
                  { icon: TrendingUp, text: 'Comissão maior: 60% para o corretor' },
                  { icon: Shield, text: 'Intermediação profissional garantida' },
                  { icon: Zap, text: 'Sem custo de captação de leads' },
                  { icon: Lock, text: 'Visitas com proteção da plataforma' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 font-medium">{item.text}</p>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => { sessionStorage.setItem('utm_content', 'corretor'); navigate('/register?role=agent'); }}
                size="lg"
                className="rounded-full h-12 px-8 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/25"
              >
                Encontrar compradores <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Modelo de Comissão */}
      <div className="bg-slate-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-semibold text-indigo-400 mb-4 uppercase tracking-wider">Modelo de comissão</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Mais vantajoso para
              <span className="block text-indigo-400 italic mt-2">quem trabalha em parceria.</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Corretores ganham mais aqui. Porque queremos que você venha buscar seus compradores.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="p-8 rounded-3xl bg-slate-800 border-slate-700">
              <p className="text-sm text-slate-400 mb-4 uppercase tracking-wider">Modelo tradicional</p>
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1">
                  <div className="h-3 bg-slate-600 rounded-full mb-2"></div>
                  <p className="text-sm text-slate-400">50% Plataforma</p>
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-slate-500 rounded-full mb-2"></div>
                  <p className="text-sm text-slate-400">50% Corretor</p>
                </div>
              </div>
            </Card>

            <Card className="p-8 rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-600 border-0">
              <p className="text-sm text-indigo-200 mb-4 uppercase tracking-wider">MatchImovel</p>
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-[0.4]">
                  <div className="h-3 bg-indigo-400 rounded-full mb-2"></div>
                  <p className="text-sm text-white">40%</p>
                </div>
                <div className="flex-[0.6]">
                  <div className="h-3 bg-white rounded-full mb-2"></div>
                  <p className="text-sm text-white font-bold">60% Você fica com mais!</p>
                </div>
              </div>
              <p className="text-sm text-indigo-100">Comissão maior para o corretor parceiro. O corretor recebe 60% do valor total.</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Curadoria Humana */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-semibold text-indigo-600 mb-4 uppercase tracking-wider">Diferenciais</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Curadoria humana.
              <span className="block text-indigo-600 italic mt-2">Tecnologia por trás.</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Não enviamos qualquer imóvel para qualquer comprador. Antes de cada apresentação, nosso curador — um corretor experiente — analisa se aquele match faz sentido real.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 rounded-3xl border-2 hover:border-indigo-300 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-2xl font-bold">0 contatos expostos</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                O contato do comprador nunca é exposto. Todo processo acontece dentro da plataforma, garantindo privacidade e proteção.
              </p>
            </Card>

            <Card className="p-8 rounded-3xl border-2 hover:border-indigo-300 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold">100% intermediado</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Proteção contra negociações diretas que prejudicam a comissão. Tudo passa pelo curador especializado.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Blog Carousel — só renderiza se houver posts publicados */}
      {blogPosts.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-24">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <p className="text-sm font-semibold text-indigo-600 mb-4 uppercase tracking-wider">Blog</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Conteúdo para ajudar
                <span className="block text-indigo-600 italic mt-2">sua decisão</span>
              </h2>
              <p className="text-slate-600 max-w-xl mx-auto">Dicas, tendências e guias do mercado imobiliário para compradores e corretores.</p>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <Card className="overflow-hidden rounded-3xl border-0 shadow-xl">
                <motion.div
                  key={currentBlogPost}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35 }}
                  className="grid grid-cols-1 md:grid-cols-2"
                >
                  {/* Image */}
                  <div className="aspect-video md:aspect-auto md:min-h-72 bg-gradient-to-br from-indigo-200 to-purple-300 overflow-hidden">
                    {blogPosts[currentBlogPost]?.cover_image_url ? (
                      <img
                        src={cloudinaryImg(blogPosts[currentBlogPost].cover_image_url, { width: 900, height: 500 })}
                        alt={blogPosts[currentBlogPost].title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl opacity-30">🏠</div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-8 md:p-10 flex flex-col justify-center bg-white">
                    {blogPosts[currentBlogPost]?.category && (
                      <span className="inline-block text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3 capitalize">
                        {blogPosts[currentBlogPost].category}
                      </span>
                    )}
                    <h3 className="text-2xl font-bold text-slate-900 mb-3 leading-snug">
                      {blogPosts[currentBlogPost]?.title}
                    </h3>
                    {blogPosts[currentBlogPost]?.excerpt && (
                      <p className="text-slate-600 leading-relaxed mb-6 line-clamp-3">
                        {blogPosts[currentBlogPost].excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-400 mb-6">
                      {blogPosts[currentBlogPost]?.published_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(blogPosts[currentBlogPost].published_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}
                        </span>
                      )}
                      {blogPosts[currentBlogPost]?.read_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {blogPosts[currentBlogPost].read_time} min
                        </span>
                      )}
                    </div>
                    <Link to={`/blog/${blogPosts[currentBlogPost]?.slug}`}>
                      <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full w-fit">
                        Ler artigo <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              </Card>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4 mt-8">
                {blogPosts.length > 1 && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-11 h-11 border-2 hover:border-indigo-600"
                    onClick={() => setCurrentBlogPost(i => (i - 1 + blogPosts.length) % blogPosts.length)}
                    aria-label="Post anterior"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                )}

                <div className="flex gap-1">
                  {blogPosts.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentBlogPost(idx)}
                      className="p-2 -m-1"
                      aria-label={`Ir para o post ${idx + 1}`}
                      aria-current={idx === currentBlogPost}
                    >
                      <span className={`block h-2 rounded-full transition-all ${idx === currentBlogPost ? 'w-8 bg-indigo-600' : 'w-2 bg-slate-300 hover:bg-slate-400'}`} />
                    </button>
                  ))}
                </div>

                {blogPosts.length > 1 && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-11 h-11 border-2 hover:border-indigo-600"
                    onClick={() => setCurrentBlogPost(i => (i + 1) % blogPosts.length)}
                    aria-label="Próximo post"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                )}
              </div>

              <div className="text-center mt-8">
                <Link to="/blog">
                  <Button variant="outline" className="rounded-full border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-8">
                    Ver todos os posts <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Depoimentos */}
      <div id="depoimentos" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-semibold text-indigo-600 mb-4 uppercase tracking-wider">Depoimentos</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              O que dizem sobre
              <span className="block text-indigo-600 italic mt-2">o MatchImovel</span>
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto relative">
            <Card className="p-12 rounded-3xl border-2 border-indigo-100 relative overflow-hidden">
              <div className="absolute top-8 left-8 text-indigo-200 text-8xl font-serif leading-none">"</div>
              
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="relative z-10"
              >
                <div className="flex gap-1 mb-6 justify-center">
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-xl text-slate-700 leading-relaxed mb-8 text-center italic">
                  {testimonials[currentTestimonial].text}
                </p>
                
                <div className="text-center">
                  <p className="font-bold text-lg text-slate-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-sm text-slate-600">{testimonials[currentTestimonial].role} • {testimonials[currentTestimonial].location}</p>
                </div>
              </motion.div>
            </Card>

            <div className="flex items-center justify-center gap-4 mt-8">
              <Button
                onClick={prevTestimonial}
                variant="outline"
                size="icon"
                className="rounded-full w-12 h-12 border-2 hover:border-indigo-600"
                data-testid="testimonial-prev"
                aria-label="Depoimento anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="flex gap-1">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx)}
                    className="p-2 -m-1"
                    data-testid={`testimonial-dot-${idx}`}
                    aria-label={`Ir para o depoimento ${idx + 1}`}
                    aria-current={idx === currentTestimonial}
                  >
                    <span className={`block h-2 rounded-full transition-all ${
                      idx === currentTestimonial
                        ? 'w-8 bg-indigo-600'
                        : 'w-2 bg-slate-300 hover:bg-slate-400'
                    }`} />
                  </button>
                ))}
              </div>

              <Button
                onClick={nextTestimonial}
                variant="outline"
                size="icon"
                className="rounded-full w-12 h-12 border-2 hover:border-indigo-600"
                data-testid="testimonial-next"
                aria-label="Próximo depoimento"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            <AppLogo className="h-6 w-auto brightness-0 invert" />
            <span className="text-xl font-bold">
              <span><span className="text-white">Match</span><span className="text-indigo-400">Imovel</span></span>
            </span>
          </div>
          <p>© 2026 MatchImovel. Todos os direitos reservados.</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <Link to="/privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link>
            <span className="text-slate-600">·</span>
            <Link to="/termos-de-uso" className="hover:text-white transition-colors">Termos de Uso</Link>
            <span className="text-slate-600">·</span>
            <button
              onClick={() => window.dispatchEvent(new Event('reopen-cookie-preferences'))}
              className="hover:text-white transition-colors"
            >
              Preferências de cookies
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
